import React from "react";
import { Box, Typography } from "@mui/material";
import { Building2, Globe, Users, Rocket, Award, MapPin } from "lucide-react";
import { useIntl } from "react-intl";

const ICON_MAP = {
  building: Building2,
  globe: Globe,
  users: Users,
  rocket: Rocket,
  award: Award,
  map_pin: MapPin,
};

const DEFAULT_ICON_SEQUENCE = [Building2, Globe, Award, Rocket, MapPin, Users, Rocket];

const resolveIcon = (iconKey, index) => {
  if (iconKey) {
    const normalized = String(iconKey).toLowerCase();
    if (ICON_MAP[normalized]) return ICON_MAP[normalized];
  }

  return DEFAULT_ICON_SEQUENCE[index % DEFAULT_ICON_SEQUENCE.length];
};

const CompanyHistoryTab = ({ milestones, companyName }) => {
  const intl = useIntl();
  const backendTimeline = (milestones || [])
    .slice()
    .sort((a, b) => (a?.year || 0) - (b?.year || 0))
    .map((milestone, index) => ({
      id: milestone.id || `milestone-${index}`,
      year: milestone.year,
      title: milestone.title || milestone.name || "Milestone",
      description: milestone.description || milestone.content || "Details coming soon.",
      icon: resolveIcon(milestone.icon, index),
    }));

  return (
    <Box sx={{ maxWidth: "48rem" }}>
      <Typography
        sx={{
          fontSize: "1.25rem",
          color: "hsl(222.2, 84%, 4.9%)",
          letterSpacing: "-0.01em",
          fontFamily: "Segoe UI Bold, system-ui, sans-serif",
          display: "inline-block",
          borderBottom: "2px solid hsl(152, 60%, 32%)",
          pb: "0.25rem",
          mb: 2.5,
        }}
      >
        {intl.formatMessage({ id: "company_view.history.title" }, { companyName: companyName || "" })}
      </Typography>

      <Box sx={{ position: "relative", ml: "1rem" }}>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "1px",
            bgcolor: "hsl(40, 8%, 88%)",
          }}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          {backendTimeline.map((event) => {
            const Icon = event.icon;

            return (
              <Box key={event.id} sx={{ position: "relative", pl: "2rem" }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    transform: "translateX(-50%)",
                    top: "0.25rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "2rem",
                    height: "2rem",
                    borderRadius: "999px",
                    border: "2px solid hsl(152, 60%, 32%)",
                    bgcolor: "hsl(0, 0%, 100%)",
                    color: "hsl(152, 60%, 32%)",
                  }}
                >
                  <Icon size={16} />
                </Box>

                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.15em",
                    color: "hsl(152, 60%, 32%)",
                    mb: "0.25rem",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  }}
                >
                  {event.year}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    color: "hsl(220, 15%, 12%)",
                    mb: "0.25rem",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                  }}
                >
                  {event.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "hsl(220, 8%, 46%)",
                    lineHeight: 1.625,
                  }}
                >
                  {event.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default CompanyHistoryTab;
